/**
 * Live HTTP implementation of the B1.10 Day Close feature API.
 *
 * Targets the Phase E1 backend surface at `/api/biz/cashier/day-close/*`
 * (see `backend/src/api/business/biz-dayclose-routes.ts`).
 *
 * Contract alignment:
 *   The backend `DayCloseSnapshot` is a **superset** of the FE Lovable
 *   contract — it adds `correlationId` (audit trace) which the wizard does
 *   not consume. Every other field matches by name AND type:
 *     • All `*_minor` fields arrive as JS numbers (BE casts via Number()).
 *     • Timestamps are ISO-8601 UTC strings.
 *     • `denominationCounts` is `Array<{ code: TryDenominationCode; count: number }>`
 *     • `prerequisites` is computed server-side (open/parked orders queried;
 *       3 checks are stubbed as 'ok' until the corresponding domains land).
 *
 *   We project the BE response into the frozen FE shape to drop the extra
 *   `correlationId` (avoids leaking trace IDs into the React Query cache
 *   and keeps the contract honest).
 *
 * Idempotency:
 *   `submitDayClose` propagates the FE-built `branchId:businessDate:attemptNo`
 *   key as both an `Idempotency-Key` header AND in the body (BE accepts
 *   either, header wins per ADR-022).
 *
 * @see day-close-api-mock.ts (fixture path)
 * @see day-close-api.ts      (mock↔live dispatcher)
 * @see backend/src/api/business/biz-dayclose-routes.ts
 */

import { ApiError } from '@/shared/api/apiClient';
import { bizGet, bizPatch, bizPost } from '@/shared/api/bizClient';

import type {
  DayCloseSnapshot,
  PatchCashCountsRequest,
  PatchDepositRequest,
  PatchNonCashCountsRequest,
  ReopenDayCloseRequest,
  StartDayCloseRequest,
  SubmitDayCloseRequest,
} from './types';

// ---------------------------------------------------------------------------
// BE → FE projection
// ---------------------------------------------------------------------------

/**
 * BE-shaped DayCloseSnapshot: identical to FE except for `correlationId`
 * which is a server-side audit field. We declare it `unknown` here to avoid
 * coupling to the backend type definition.
 */
type BackendSnapshot = DayCloseSnapshot & { readonly correlationId?: unknown };

function adaptSnapshot(snap: BackendSnapshot): DayCloseSnapshot {
  // Strip BE-only fields. Spread + omit pattern keeps `readonly` semantics
  // intact and explicitly drops `correlationId` rather than leaking it
  // through a cast.
  const { correlationId: _ignored, ...rest } = snap;
  void _ignored;
  return rest as DayCloseSnapshot;
}

// ---------------------------------------------------------------------------
// Public API — mirrors day-close-api-mock.ts surface
// ---------------------------------------------------------------------------

export async function startDayClose(
  req: StartDayCloseRequest,
): Promise<DayCloseSnapshot> {
  const snap = await bizPost<BackendSnapshot, StartDayCloseRequest>(
    '/biz/cashier/day-close/start',
    {
      branchId: req.branchId,
      ...(req.cashierSessionId ? { cashierSessionId: req.cashierSessionId } : {}),
    },
  );
  return adaptSnapshot(snap);
}

export async function getDayCloseSnapshot(
  sessionId: string,
): Promise<DayCloseSnapshot> {
  const snap = await bizGet<BackendSnapshot>(
    `/biz/cashier/day-close/${sessionId}`,
  );
  return adaptSnapshot(snap);
}

/**
 * Optional helper — the backend exposes `/active` to resume an in-flight
 * session for a branch (useful when the wizard is re-opened mid-flow).
 * The mock contract doesn't need this since its store is module-local;
 * pages that want resume support can call this directly off `httpApi`.
 *
 * Returns `null` when the BE responds 404 (no active session for branch).
 */
export async function getActiveDayClose(
  branchId: string,
): Promise<DayCloseSnapshot | null> {
  try {
    const snap = await bizGet<BackendSnapshot>(
      '/biz/cashier/day-close/active',
      { params: { branchId } },
    );
    return adaptSnapshot(snap);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

export async function patchCashCounts(
  req: PatchCashCountsRequest,
): Promise<DayCloseSnapshot> {
  const snap = await bizPatch<BackendSnapshot, { counts: PatchCashCountsRequest['counts'] }>(
    `/biz/cashier/day-close/${req.sessionId}/cash-counts`,
    { counts: req.counts },
  );
  return adaptSnapshot(snap);
}

export async function patchNonCashCounts(
  req: PatchNonCashCountsRequest,
): Promise<DayCloseSnapshot> {
  const snap = await bizPatch<
    BackendSnapshot,
    { tenders: PatchNonCashCountsRequest['tenders'] }
  >(
    `/biz/cashier/day-close/${req.sessionId}/non-cash-counts`,
    { tenders: req.tenders },
  );
  return adaptSnapshot(snap);
}

export async function patchDeposit(
  req: PatchDepositRequest,
): Promise<DayCloseSnapshot> {
  const snap = await bizPatch<
    BackendSnapshot,
    { closingFloatMinor: number; depositAmountMinor: number }
  >(`/biz/cashier/day-close/${req.sessionId}/deposit`, {
    closingFloatMinor: req.closingFloatMinor,
    depositAmountMinor: req.depositAmountMinor,
  });
  return adaptSnapshot(snap);
}

export async function submitDayClose(
  req: SubmitDayCloseRequest,
): Promise<DayCloseSnapshot> {
  const snap = await bizPost<
    BackendSnapshot,
    { idempotencyKey: string; override: SubmitDayCloseRequest['override'] }
  >(
    `/biz/cashier/day-close/${req.sessionId}/submit`,
    { idempotencyKey: req.idempotencyKey, override: req.override },
    {
      headers: { 'Idempotency-Key': req.idempotencyKey },
    },
  );
  return adaptSnapshot(snap);
}

export async function reopenDayClose(
  req: ReopenDayCloseRequest,
): Promise<DayCloseSnapshot> {
  const snap = await bizPost<
    BackendSnapshot,
    { reason: string; explanation: string | null }
  >(`/biz/cashier/day-close/${req.sessionId}/reopen`, {
    reason: req.reason,
    explanation: req.explanation,
  });
  return adaptSnapshot(snap);
}

/**
 * Helper: generate idempotency-key per ADR-022 / ADR-110 contract.
 * Format: `branchId:businessDate:attemptNo`.
 *
 * Re-exported here so callers using the live adapter can stay consistent
 * with the mock module's helper signature.
 */
export function buildIdempotencyKey(
  branchId: string,
  businessDate: string,
  attemptNo: number,
): string {
  return `${branchId}:${businessDate}:${attemptNo}`;
}
